import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { Box, Typography, Paper } from '@mui/material';
import { DealershipPrice, DealershipStats, VehicleStats, StateStats, formatPrice } from '../../services/analyticsService';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface ChartProps {
  title: string;
  data: any;
  options: ChartOptions<'bar'>;
}

const ChartContainer: React.FC<ChartProps> = ({ title, data, options }) => (
  <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1976d2' }}>
      {title}
    </Typography>
    <Box sx={{ height: 400, position: 'relative' }}>
      <Bar data={data} options={options} />
    </Box>
  </Paper>
);

interface AnalyticsChartsProps {
  dealershipPrices: DealershipPrice[];
  dealershipReportCounts: DealershipStats[];
  dealershipUserCounts: DealershipStats[];
  vehicleMakes: VehicleStats[];
  vehicleModels: VehicleStats[];
  stateStats: StateStats[];
}

const AnalyticsCharts: React.FC<AnalyticsChartsProps> = ({
  dealershipPrices,
  dealershipReportCounts,
  dealershipUserCounts,
  vehicleMakes,
  vehicleModels,
  stateStats,
}) => {
  // Common chart options
  const baseOptions: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: '#e0e0e0',
        },
        ticks: {
          font: {
            size: 11,
          },
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          maxRotation: 45,
          font: {
            size: 10,
          },
        },
      },
    },
  };

  // 1. Total Price per Dealership Chart
  // Filter out extreme outliers to improve chart readability
  const averageValue = dealershipPrices.reduce((sum, d) => sum + d.totalPrice, 0) / dealershipPrices.length;
  const outlierThreshold = averageValue * 10; // Values more than 10x average are considered outliers
  
  
  // Create two versions: with and without outliers
  const top15Dealerships = dealershipPrices.slice(0, 15);
  const top15WithoutOutliers = dealershipPrices.filter(d => d.totalPrice <= outlierThreshold).slice(0, 15);
  
  
  
  // Use the filtered data for better visualization
  const chartDealerships = top15WithoutOutliers.length >= 10 ? top15WithoutOutliers : top15Dealerships;

  const dealershipPriceData = {
    labels: chartDealerships.map(item => 
      item.dealership.length > 30 ? `${item.dealership.substring(0, 30)}...` : item.dealership
    ),
    datasets: [
      {
        data: chartDealerships.map(item => item.totalPrice),
        backgroundColor: 'rgba(25, 118, 210, 0.8)',
        borderColor: 'rgba(25, 118, 210, 1)',
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  };
  

  const dealershipPriceOptions: ChartOptions<'bar'> = {
    ...baseOptions,
    plugins: {
      ...baseOptions.plugins,
      tooltip: {
        callbacks: {
          label: (context) => {
            const item = chartDealerships[context.dataIndex];
            return [
              `Total: ${formatPrice(item.totalPrice)}`,
              `Average: ${formatPrice(item.averagePrice)}`,
              `Reports: ${item.reportCount}`,
            ];
          },
        },
      },
    },
    scales: {
      ...baseOptions.scales,
      y: {
        beginAtZero: true,
        grid: {
          color: '#e0e0e0',
        },
        ticks: {
          font: {
            size: 11,
          },
          callback: (value) => formatPrice(Number(value)),
          maxTicksLimit: 8,
        },
      },
    },
  };

  // 2. Report Count per Dealership Chart
  const reportCountData = {
    labels: dealershipReportCounts.slice(0, 15).map(item => 
      item.dealership.length > 30 ? `${item.dealership.substring(0, 30)}...` : item.dealership
    ),
    datasets: [
      {
        data: dealershipReportCounts.slice(0, 15).map(item => item.reportCount),
        backgroundColor: 'rgba(76, 175, 80, 0.8)',
        borderColor: 'rgba(76, 175, 80, 1)',
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  };

  const reportCountOptions: ChartOptions<'bar'> = {
    ...baseOptions,
    plugins: {
      ...baseOptions.plugins,
      tooltip: {
        callbacks: {
          label: (context) => {
            const item = dealershipReportCounts[context.dataIndex];
            return [
              `Reports: ${item.reportCount}`,
              `Unique Users: ${item.uniqueUsers}`,
            ];
          },
        },
      },
    },
  };

  // 3. Unique Users per Dealership Chart
  const uniqueUsersData = {
    labels: dealershipUserCounts.slice(0, 15).map(item => 
      item.dealership.length > 30 ? `${item.dealership.substring(0, 30)}...` : item.dealership
    ),
    datasets: [
      {
        data: dealershipUserCounts.slice(0, 15).map(item => item.uniqueUsers),
        backgroundColor: 'rgba(255, 152, 0, 0.8)',
        borderColor: 'rgba(255, 152, 0, 1)',
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  };

  const uniqueUsersOptions: ChartOptions<'bar'> = {
    ...baseOptions,
    plugins: {
      ...baseOptions.plugins,
      tooltip: {
        callbacks: {
          label: (context) => {
            const item = dealershipUserCounts[context.dataIndex];
            return [
              `Unique Users: ${item.uniqueUsers}`,
              `Total Reports: ${item.reportCount}`,
            ];
          },
        },
      },
    },
  };

  // 4. Vehicle Makes Chart
  const vehicleMakesData = {
    labels: vehicleMakes.slice(0, 15).map(item => item.make),
    datasets: [
      {
        data: vehicleMakes.slice(0, 15).map(item => item.count),
        backgroundColor: 'rgba(156, 39, 176, 0.8)',
        borderColor: 'rgba(156, 39, 176, 1)',
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  };

  const vehicleMakesOptions: ChartOptions<'bar'> = {
    ...baseOptions,
    plugins: {
      ...baseOptions.plugins,
      tooltip: {
        callbacks: {
          label: (context) => {
            const item = vehicleMakes[context.dataIndex];
            return `${item.make}: ${item.count} reports`;
          },
        },
      },
    },
  };

  // 5. Vehicle Models Chart (top 20)
  const vehicleModelsData = {
    labels: vehicleModels.slice(0, 20).map(item => 
      item.model.length > 25 ? `${item.model.substring(0, 25)}...` : item.model
    ),
    datasets: [
      {
        data: vehicleModels.slice(0, 20).map(item => item.count),
        backgroundColor: 'rgba(244, 67, 54, 0.8)',
        borderColor: 'rgba(244, 67, 54, 1)',
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  };

  const vehicleModelsOptions: ChartOptions<'bar'> = {
    ...baseOptions,
    plugins: {
      ...baseOptions.plugins,
      tooltip: {
        callbacks: {
          label: (context) => {
            const item = vehicleModels[context.dataIndex];
            return `${item.model}: ${item.count} reports`;
          },
        },
      },
    },
  };

  // 6. Reports by State Chart
  const stateReportsData = {
    labels: stateStats.slice(0, 15).map(item => item.state),
    datasets: [
      {
        data: stateStats.slice(0, 15).map(item => item.reportCount),
        backgroundColor: 'rgba(63, 81, 181, 0.8)',
        borderColor: 'rgba(63, 81, 181, 1)',
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  };

  const stateReportsOptions: ChartOptions<'bar'> = {
    ...baseOptions,
    plugins: {
      ...baseOptions.plugins,
      tooltip: {
        callbacks: {
          label: (context) => {
            const item = stateStats[context.dataIndex];
            return [
              `Reports: ${item.reportCount}`,
              `Unique Users: ${item.uniqueUsers}`,
              `Dealerships: ${item.totalDealerships}`,
            ];
          },
        },
      },
    },
  };

  // 7. Unique Users by State Chart
  const stateUsersData = {
    labels: stateStats.slice(0, 15).map(item => item.state),
    datasets: [
      {
        data: stateStats.slice(0, 15).map(item => item.uniqueUsers),
        backgroundColor: 'rgba(121, 85, 72, 0.8)',
        borderColor: 'rgba(121, 85, 72, 1)',
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  };

  const stateUsersOptions: ChartOptions<'bar'> = {
    ...baseOptions,
    plugins: {
      ...baseOptions.plugins,
      tooltip: {
        callbacks: {
          label: (context) => {
            const item = stateStats[context.dataIndex];
            return [
              `Unique Users: ${item.uniqueUsers}`,
              `Total Reports: ${item.reportCount}`,
              `Dealerships: ${item.totalDealerships}`,
            ];
          },
        },
      },
    },
  };

  return (
    <Box>
      {/* First Row */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            lg: '1fr 1fr'
          },
          gap: 3,
          mb: 3
        }}
      >
        {/* Total Price per Dealership */}
        <ChartContainer
          title={chartDealerships.length < top15Dealerships.length ? 
            "Total Price per Dealership (Outliers Excluded)" : 
            "Total Price per Dealership"}
          data={dealershipPriceData}
          options={dealershipPriceOptions}
        />

        {/* Report Count per Dealership */}
        <ChartContainer
          title="Number of Reports per Dealership"
          data={reportCountData}
          options={reportCountOptions}
        />
      </Box>

      {/* Second Row */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            lg: '1fr 1fr'
          },
          gap: 3,
          mb: 3
        }}
      >
        {/* Unique Users per Dealership */}
        <ChartContainer
          title="Unique Users per Dealership"
          data={uniqueUsersData}
          options={uniqueUsersOptions}
        />

        {/* Vehicle Makes */}
        <ChartContainer
          title="Reports by Vehicle Make"
          data={vehicleMakesData}
          options={vehicleMakesOptions}
        />
      </Box>

      {/* Third Row - Full Width */}
      <Box sx={{ mb: 3 }}>
        <ChartContainer
          title="Reports by Vehicle Model (Top 20)"
          data={vehicleModelsData}
          options={vehicleModelsOptions}
        />
      </Box>

      {/* Fourth Row - State Analytics */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            lg: '1fr 1fr'
          },
          gap: 3,
          mb: 3
        }}
      >
        {/* Reports by State */}
        <ChartContainer
          title="Reports by State"
          data={stateReportsData}
          options={stateReportsOptions}
        />

        {/* Unique Users by State */}
        <ChartContainer
          title="Unique Users by State"
          data={stateUsersData}
          options={stateUsersOptions}
        />
      </Box>
    </Box>
  );
};

export default AnalyticsCharts;